import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { FacilityProvider } from './context/FacilityContext';
import { PublicFacilityProvider } from './context/PublicFacilityContext';
import ProtectedRoute from './components/ProtectedRoute';
import SubdomainRedirect from './components/SubdomainRedirect';
import Layout from './components/Layout';
import { ErrorBoundary } from './observability';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AdminLogin from './pages/AdminLogin';
import AcceptInvite from './pages/AcceptInvite';
import RentFlow from './pages/RentFlow';
import RentThanks from './pages/RentThanks';
import PortalLogin from './pages/PortalLogin';
import PortalDashboard from './pages/PortalDashboard';
import PortalPay from './pages/PortalPay';
import PortalAutopay from './pages/PortalAutopay';
import LeaseSign from './pages/LeaseSign';
import PublicLeaseSign from './pages/PublicLeaseSign';
import PublicLeaseSignDone from './pages/PublicLeaseSignDone';
import Dashboard from './pages/Dashboard';
import CustomerSearch from './pages/CustomerSearch';
import CustomerForm from './pages/CustomerForm';
import UnitsPage from './pages/UnitsPage';
import UnitForm from './pages/UnitForm';
import UnitBulkForm from './pages/UnitBulkForm';
import MassRateChange from './pages/MassRateChange';
import ReservationsList from './pages/ReservationsList';
import ReservationNew from './pages/ReservationNew';
import RentalsPage from './pages/RentalsPage';
import RentalForm from './pages/RentalForm';
// Payments + Invoices restored as top-level pages (2026-06-05). The
// earlier 2026-06-01 consolidation tucked them under /reports?view=…
// as sub-tabs; we've unwound that so /payments and /invoices mount
// the dedicated list pages directly. Reports.js now owns ONLY the
// KPIs/catalog surface.
import Payments from './pages/Payments';
import Invoices from './pages/Invoices';
import PaymentForm from './pages/PaymentForm';
import TakePayment from './pages/TakePayment';
import InvoiceForm from './pages/InvoiceForm';
import Reports from './pages/Reports';
import GateActivityLog from './pages/GateActivityLog';
import Assistant from './pages/Assistant';
// Automation page deleted 2026-06-03; the surface lives in Settings under
// Communication -> Automation (pages/AutomationSection.js). The old
// AutomationRules component lingered on /settings/automation for bookmarks
// and became a SECOND copy of the same screen — edits to it were invisible,
// because that is not the route anyone uses. Removed 2026-07-29; the path now
// redirects to the live surface so old bookmarks still work.
import Portfolio from './pages/Portfolio';
import StaleVersionBanner from './components/StaleVersionBanner';
import Intelligence from './pages/Intelligence';
import WebsiteSection from './pages/WebsiteSection';
import MarketingSection from './pages/MarketingSection';
import Marketplace from './pages/Marketplace';
import Auctions from './pages/Auctions';
import ManualEsign from './pages/ManualEsign';
import SendEsign from './pages/SendEsign';
import PublicSignDoc from './pages/PublicSignDoc';
import Cameras from './pages/Cameras';
import SmsAi from './pages/SmsAi';
import DocumentSigning from './pages/DocumentSigning';
import Settings from './pages/Settings';
import FacilityForm from './pages/FacilityForm';
// Fees + Late/Lien are now consolidated into a single tabbed settings page
// (FeesSettings) mounted at /settings/fees. Fees.js / LateLien.js are still
// the underlying tab bodies — imported by FeesSettings, not directly here.
import GateTab from './pages/automation/GateTab';
import Projects from './pages/Projects';
import Tasks from './pages/Tasks';
import CustomFieldsList from './pages/CustomFieldsList';
import CustomFieldEditor from './pages/CustomFieldEditor';
import FeesSettings from './pages/FeesSettings';
import TeamUsers from './pages/TeamUsers';
import TaxRates from './pages/TaxRates';
// Promotions is now mounted as a tab inside UnitsPage (/units?view=promotions),
// not directly here — the old /settings/promotions route redirects there.
import StripeConnect from './pages/StripeConnect';
import StorageAgreements from './pages/StorageAgreements';
import PortfolioExport from './pages/PortfolioExport';
import UnitHistory from './pages/UnitHistory';
import UnitDetail from './pages/UnitDetail';
import MapSetup from './pages/MapSetup';
import PlatformAdmin from './pages/PlatformAdmin';
import HelpCenter from './pages/HelpCenter';

const HomeRedirect = () => {
  const { isPlatformUser, impersonation } = useAuth();
  const target = isPlatformUser() && !impersonation ? '/platform-admin' : '/dashboard';
  return <Navigate to={target} replace />;
};

// There is no read-only customer detail page; the customer record IS
// the editable form. Any /customers/:id link resolves to the edit view
// so a bare id never lands on a blank route.
// Communication moved into Settings (2026-06-01). Old /communication
// bookmarks redirect into the Settings tab while preserving any
// ?view=email/text/settings sub-tab query string so the user lands on
// the same place they expected.
const CommunicationRedirect = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', 'communication');
  return <Navigate to={`/settings?${params.toString()}`} replace />;
};

// Route-keyed error boundary. If a thrown render error trips Sentry's
// ErrorBoundary (e.g. an unmount-race in the rent wizard during a cross-
// document back-nav), navigating to a different route will re-mount the
// boundary and clear the sticky "Something went wrong" fallback without
// requiring a hard reload. Keyed on pathname only -- hash changes inside
// a page (e.g. the wizard's own #personal/#rental/#pay nav) do not
// re-mount and therefore do not disturb the happy path.
const RouteErrorBoundary = ({ children }) => {
  const { pathname } = useLocation();
  const Fallback = () => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ fontSize: 13, color: '#64748b' }}>
        Something went wrong. Please refresh the page.
      </p>
    </div>
  );
  return (
    <ErrorBoundary key={pathname} fallback={<Fallback />}>
      {children}
    </ErrorBoundary>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors closeButton />
      <StaleVersionBanner />
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
          <PublicFacilityProvider>
          <FacilityProvider>
            <SubdomainRedirect />
            <RouteErrorBoundary>
            <Routes>
              {/* /login is operator-only at app.mysms360.com. Tenants
                  who land here from a facility subdomain (e.g. {facility}.
                  mysms360.com — via the "Make Payment / Login" button on
                  the marketing site) get redirected to the tenant /portal
                  login. Any host that isn't app./www./localhost is
                  treated as a tenant facility subdomain. */}
              <Route
                path="/login"
                element={
                  (() => {
                    const host = (typeof window !== 'undefined' && window.location.hostname) || '';
                    const isOperatorHost =
                      host.startsWith('app.') ||
                      host.startsWith('www.') ||
                      host === 'mysms360.com' ||
                      host === 'localhost' ||
                      host.startsWith('127.0.0.1');
                    return isOperatorHost ? <Login /> : <Navigate to="/portal" replace />;
                  })()
                }
              />
              {/* /signup is a sign-up REQUEST, operator hosts only. Accounts
                  are still provisioned by staff (the 2026-07-25 decision
                  stands — two tenants once signed themselves up as facility
                  owners): the page captures who / brand / locations, welcomes
                  the prospect by email and alerts the platform inbox. A tenant
                  on a facility subdomain goes to the tenant portal instead. */}
              <Route
                path="/signup"
                element={
                  (() => {
                    const host = (typeof window !== 'undefined' && window.location.hostname) || '';
                    const isOperatorHost =
                      host.startsWith('app.') ||
                      host.startsWith('www.') ||
                      host === 'mysms360.com' ||
                      host === 'localhost' ||
                      host.startsWith('127.0.0.1');
                    return isOperatorHost ? <Signup /> : <Navigate to="/portal" replace />;
                  })()
                }
              />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              {/* /rent with no slug resolves the slug from the facility
                  subdomain ({facility}.mysms360.com -> "{facility}") via the
                  PublicFacilityContext. /rent/:slug works for direct
                  links to a specific facility (e.g. shared on social). */}
              {/* /rent/thanks is the post-Stripe success URL on the
                  facility subdomain — operator-branded, no marketing
                  chrome. /rent/:slug/thanks below is the legacy form
                  retained for already-issued Stripe sessions. */}
              <Route path="/rent/thanks" element={<RentThanks />} />
              <Route path="/rent" element={<RentFlow />} />
              <Route path="/rent/:slug" element={<RentFlow />} />
              <Route path="/rent/:slug/thanks" element={<RentThanks />} />
              {/* Legacy single-page rent flow kept at /rent-legacy for
                  rollback. Remove after RentFlow has been verified live
                  on every facility subdomain. */}
              <Route path="/portal" element={<PortalLogin />} />
              <Route path="/portal/dashboard" element={<PortalDashboard />} />
              <Route path="/portal/pay" element={<PortalPay />} />
              <Route path="/portal/autopay" element={<PortalAutopay />} />
              <Route path="/sign/:token" element={<LeaseSign />} />
              {/* Native E-SIGN Act compliant lease signing (token-auth,
                  no JWT, public). The legacy /sign/:token above is the
                  earlier typed-name primitive; /lease/sign/:token is the
                  full audit-trail + audit-page flow. */}
              <Route path="/sign-doc/:token" element={<PublicSignDoc />} />
              <Route path="/lease/sign/:token" element={<PublicLeaseSign />} />
              <Route path="/lease/sign/:token/done" element={<PublicLeaseSignDone />} />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<HomeRedirect />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="customers" element={<CustomerSearch />} />
                <Route path="customers/new" element={<CustomerForm />} />
                <Route path="customers/:id/edit" element={<CustomerForm />} />
                <Route path="customers/:id" element={<CustomerForm />} />
                <Route path="units" element={<UnitsPage />} />
                <Route path="units/new" element={<UnitForm />} />
                <Route path="units/bulk" element={<UnitBulkForm />} />
                <Route path="units/mass-edit-rates" element={<MassRateChange />} />
                <Route path="projects" element={<Projects />} />
                <Route path="tasks" element={<Tasks />} />
                <Route path="reservations" element={<ReservationsList />} />
                <Route path="reservations/new" element={<ReservationNew />} />
                <Route path="units/:id/edit" element={<UnitForm />} />
                <Route path="units/:id" element={<UnitDetail />} />
                <Route path="rentals" element={<RentalsPage />} />
                <Route path="rentals/new" element={<RentalForm />} />
                <Route path="rentals/:id/edit" element={<RentalForm />} />
                {/* Invoices + Payments are top-level list pages
                    (restored 2026-06-05). Reports owns KPIs only. */}
                <Route path="payments" element={<Payments />} />
                <Route path="payments/new" element={<PaymentForm />} />
                <Route path="payments/:id/edit" element={<PaymentForm />} />
                <Route path="payments/take" element={<TakePayment />} />
                {/* Lead Management lives as the Leads tab of
                    Leads and Reservations (owner, 2026-08-07). */}
                <Route path="leads" element={<Navigate to="/reservations?tab=leads" replace />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="invoices/new" element={<InvoiceForm />} />
                <Route path="invoices/:id/edit" element={<InvoiceForm />} />
                <Route path="reports" element={<Reports />} />
                <Route path="help" element={<HelpCenter />} />
                <Route path="reports/gate-activity" element={<GateActivityLog />} />
                <Route path="assistant" element={<Assistant />} />
                <Route path="site-map" element={<Navigate to="/units?view=map" replace />} />
                <Route path="automation" element={<Navigate to="/settings?tab=automation" replace />} />
                <Route
                  path="portfolio"
                  element={
                    <ProtectedRoute requirePortfolioOwner={true}>
                      <Portfolio />
                    </ProtectedRoute>
                  }
                />
                <Route path="intelligence" element={<Intelligence />} />
                <Route path="website" element={<WebsiteSection />} />
                <Route path="marketing" element={<MarketingSection />} />
                <Route path="auctions" element={<Auctions />} />
                <Route path="manual-esign" element={<ManualEsign />} />
                <Route path="send-esign" element={<SendEsign />} />
                <Route path="document-signing" element={<DocumentSigning />} />
                <Route path="marketplace" element={<Marketplace />} />
                <Route path="communication" element={<CommunicationRedirect />} />
                <Route path="sms-ai" element={<SmsAi />} />
                {/* /cameras kept as back-compat redirect into the
                    Camera Intelligence sub-tab on the new SMS AI page. */}
                <Route path="cameras" element={<Navigate to="/sms-ai?view=camera" replace />} />
                <Route path="platform-admin" element={<PlatformAdmin />} />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/facility"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <FacilityForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/facility/new"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <FacilityForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/fees"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <FeesSettings />
                    </ProtectedRoute>
                  }
                />
                {/* Gate controllers are FACILITY hardware setup — which vendor,
                    code length, how a tenant's code is generated — not tenant
                    communication. It briefly lived inside the Automation tab,
                    which put it under Communication once Automation moved
                    there; this gives it its own route under Settings. */}
                {/* Custom fields: the editor pages existed but were never
                    routed, so the Settings tile linked nowhere and the whole
                    feature was unreachable. */}
                <Route
                  path="settings/custom-fields"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <CustomFieldsList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/custom-fields/new"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <CustomFieldEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/custom-fields/:id"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <CustomFieldEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/gate"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <GateTab />
                    </ProtectedRoute>
                  }
                />
                {/* Late/Lien consolidated into the combined Fees page as the
                    "Late / Lien" tab. Old bookmarks redirect into that tab. */}
                <Route
                  path="settings/late-lien"
                  element={<Navigate to="/settings/fees?tab=late-lien" replace />}
                />
                <Route
                  path="settings/users"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <TeamUsers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/tax-rates"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <TaxRates />
                    </ProtectedRoute>
                  }
                />
                {/* Promotions moved into the Units nav as a tab
                    (/units?view=promotions) 2026-06-19. Old bookmarks
                    redirect there so existing links still land. */}
                <Route
                  path="settings/promotions"
                  element={<Navigate to="/units?view=promotions" replace />}
                />
                <Route
                  path="settings/stripe"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <StripeConnect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/automation"
                  element={
                    <Navigate
                      to="/settings?tab=communication&mode=automation"
                      replace
                    />
                  }
                />
                <Route
                  path="settings/storage-agreements"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <StorageAgreements />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/portfolio-export"
                  element={
                    <ProtectedRoute>
                      <PortfolioExport />
                    </ProtectedRoute>
                  }
                />
                {/* Impersonation is a tab of Users & Roles now
                    (owner, 2026-08-06); old bookmarks land there. */}
                <Route
                  path="settings/impersonation"
                  element={<Navigate to="/settings/users?tab=impersonation" replace />}
                />
                <Route
                  path="units/:unitId/history"
                  element={
                    <ProtectedRoute>
                      <UnitHistory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="units/map-setup"
                  element={
                    <ProtectedRoute requireManager={true}>
                      <MapSetup />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Routes>
            </RouteErrorBoundary>
          </FacilityProvider>
          </PublicFacilityProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
